import { Controller } from '@hotwired/stimulus'
import ScrollAnimationObserver from '~/javascript/services/scroll_animation_observer'

/**
 * Stimulus controller wrapper for ScrollAnimationObserver
 *
 * Usage:
 *   <div data-controller="scroll-animation"
 *        data-scroll-animation-threshold-value="0.2"
 *        data-scroll-animation-replay-value="true">
 *     <div class="animate-on-scroll" data-animation="fade-in-up">
 *       Content that animates on scroll
 *     </div>
 *   </div>
 *
 * Or on the element itself:
 *   <div data-controller="scroll-animation"
 *        data-scroll-animation-self-value="true"
 *        class="animate-on-scroll" data-animation="fade-in-up">
 *     This element animates itself
 *   </div>
 */
export default class extends Controller {
  static values = {
    // IntersectionObserver threshold (0-1)
    threshold: { type: Number, default: 0.1 },

    // Root margin for triggering earlier/later
    rootMargin: { type: String, default: '0px 0px -50px 0px' },

    // Whether to replay animations when elements re-enter viewport
    replay: { type: Boolean, default: false },

    // Base delay in ms for staggered animations
    staggerDelay: { type: Number, default: 100 },

    // Selector for elements to observe (within this controller's scope)
    selector: { type: String, default: '[data-animate-on-scroll], .animate-on-scroll' },

    // Whether to observe the controller element itself
    self: { type: Boolean, default: false },

    // Respect prefers-reduced-motion
    respectReducedMotion: { type: Boolean, default: true }
  }

  connect() {
    this.observer = new ScrollAnimationObserver({
      threshold: this.thresholdValue,
      rootMargin: this.rootMarginValue,
      replay: this.replayValue,
      staggerDelay: this.staggerDelayValue,
      selector: this.selectorValue,
      respectReducedMotion: this.respectReducedMotionValue
    })

    // If self mode, observe this element directly
    if (this.selfValue) {
      this.observer.observe(document)
      this.observer.observeElement(this.element)
    } else {
      // Observe children within this element
      this.observer.observe(this.element)
    }

    // Store bound event handlers so they can be properly removed
    this._boundHandleAnimateIn = this.handleAnimateIn.bind(this)
    this._boundHandleAnimateOut = this.handleAnimateOut.bind(this)

    // Listen for custom events
    this.element.addEventListener('scroll-animate-in', this._boundHandleAnimateIn)
    this.element.addEventListener('scroll-animate-out', this._boundHandleAnimateOut)
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    // Remove event listeners using stored bound references
    if (this._boundHandleAnimateIn) {
      this.element.removeEventListener('scroll-animate-in', this._boundHandleAnimateIn)
    }
    if (this._boundHandleAnimateOut) {
      this.element.removeEventListener('scroll-animate-out', this._boundHandleAnimateOut)
    }
  }

  // Action: Manually trigger animation for a specific element
  trigger(event) {
    const target = event.target.closest(this.selectorValue) || event.target
    this.observer.trigger(target)
  }

  // Action: Reset animation state for a specific element
  reset(event) {
    const target = event.target.closest(this.selectorValue) || event.target
    this.observer.reset(target)
  }

  // Action: Refresh observation (useful after dynamic content loads)
  refresh() {
    this.observer.refresh(this.element)
  }

  // Event handler for animate-in
  handleAnimateIn(event) {
    // Dispatch a Stimulus action if needed
    this.dispatch('in', { detail: event.detail })
  }

  // Event handler for animate-out
  handleAnimateOut(event) {
    // Dispatch a Stimulus action if needed
    this.dispatch('out', { detail: event.detail })
  }

  // Value changed callbacks for live updates
  thresholdValueChanged() {
    this.reconnectObserver()
  }

  rootMarginValueChanged() {
    this.reconnectObserver()
  }

  replayValueChanged() {
    this.reconnectObserver()
  }

  staggerDelayValueChanged() {
    this.reconnectObserver()
  }

  reconnectObserver() {
    if (!this.observer) return

    this.observer.disconnect()

    this.observer = new ScrollAnimationObserver({
      threshold: this.thresholdValue,
      rootMargin: this.rootMarginValue,
      replay: this.replayValue,
      staggerDelay: this.staggerDelayValue,
      selector: this.selectorValue,
      respectReducedMotion: this.respectReducedMotionValue
    })

    if (this.selfValue) {
      this.observer.observe(document)
      this.observer.observeElement(this.element)
    } else {
      this.observer.observe(this.element)
    }
  }
}
